// 168B §1a — THE ESLINT RATCHET'S OWN REMEDY. This file sat at its
// baseline exactly (504 of 504 warnings, zero headroom), and 73 of those are
// `react/react-in-jsx-scope` — one per JSX element, so a single new element
// here reddens `scripts/lint/eslint-ratchet.js`. Importing React is what the
// rule asks for; the JSX transform stays automatic (vite.config.js), so this
// changes no emitted code and no rendered byte — the 57c replay is the proof.
import React from "react";
import type { Dispatch } from "react"; // 189 §H
import { I, pname } from "../utils";
import { FS, RAD, Z } from "../lib/tokens";
import type { TFn } from "../lib/taxonomyTypes"; // 204 §6 — `t` narrowed at one seam
import { Download, Folder, Search, SquareKanban, Trash2, Upload, Users } from "lucide-react";
import { loadXlsx } from "../lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is written
import { xlsxRow } from "../lib/xlsxCells.mjs";
import { AddButton, LockButton, SegmentedControl, OPEN_MODE_GLOW, OPEN_MODE_ICON, HScroll, SortCaret } from "./SharedUI"; // [AA] — the lock's glow; 190 round 7 [AH] — the strip's own scroll affordance
import Table from "./Table";
import { BentoPickerAnchor } from "./BentoBoard"; // 190 round 9 [AS] — the picker beside or below its icon

// Brief 57 §3a — THE COMPONENT KNOWS NO ENTITY. Every `et === "..."` that
// lived here is now a fact on the entity's ecfg description
// (src/lib/ecfg.tsx): a per-entity card, gate, filter axis or delete target is
// DECLARED by the entity and read here through a named capability. `et`
// survives only as the key under which per-entity i18n keys and persisted
// sort state are filed — an identity, never a branch.
//
// The capability fields, and where each came from:
//   label                the empty-state noun (was an 11-entry map here)
//   canEditRow           collaborator edit gate (C74c)
//   canDeleteRow         collaborator delete gate (C74c)
//   deleteConfirm        bespoke destructive wording + banner shape (05k §2)
//   onDelete             the delete target (was an 11-entry SK map here)
//   detailBadge          archived badge (Phase 36 D)
//   archiveAction        archive / unarchive button (Phase 36 D)
//   casesCard            enriched matters card (Phase 43 item 3 / D22)
//   contactPersonsCard · linkedClientCard · opponentContactsCard ·
//   linkedOpponentCard · weeklyScheduleCard · internalRatesCard
//                        the six detail-grid slots, IN A FROZEN ORDER — see
//                        the note on `false` below
//   detailFooter         the CDD section (Phase 9)
//   archivedVisibility   archived-row axis (Phase 43 item 1)
//   rowPass              structured filter predicate (Phase 43 item 1)
//   searchText           211 §19 — text the search finds a row by beyond its own
//                        fields (a client's numbers in its collective cases)
//   extraCols            filter-driven extra columns (Phase 43 item 8)
//   rowCtx               168B §1b — WAS `extraRowCtx`, and the name stopped
//                        being true. It is the per-row context the ENTITY
//                        declares, computed ONCE per row per render and handed
//                        to every column's `r`/`sv` as well as to the extra
//                        columns' cells. The Clients list's four computed
//                        columns all read one `clientMatterSummary`; without
//                        the memo below each of them would recompute it, and
//                        the comparator would recompute it per COMPARISON.
//   cols.t[].gate        168B §1e — a column may declare `gate(ctx)`; a false
//                        answer removes it from the header, from every row and
//                        from the letter separators' colSpan. The Clients
//                        list's Amount-due column is absent, not blank, for a
//                        viewer who may see no figures at all.
//   casesCol             168B §1b — `false` declines the generic trailing
//                        Cases column, for an entity that declares a Cases
//                        column of its own. Absent (every entity but `cl`)
//                        keeps it exactly as it was.
//   caseCountExtra       extra matters counted into the generic Cases column
//   exportSheet          brief 57 §3b — THE EXPORT, AS A DECLARED CAPABILITY.
//                        An entity declares { filename, sheetName, row,
//                        exportedToast, failedToast } or does not have one;
//                        the engine at the bottom of this file is the ONE
//                        shared mechanism: visible rows only (`fi` — the
//                        LEGAL-LANE ruling), every cell through the xlsxRow
//                        cap guard, failure a toast and never silence. None
//                        of the thirteen declares one today, so their trees
//                        are unchanged — the wrapper returns the undecorated
//                        body when the field is absent, which is what the
//                        byte-identity freeze holds.
//   preList · toolbar · rowsSource · sortCtl · plainRows
//                        brief 57 §3c — THE LIST SHELL, for a list that is not
//                        an alphabetical directory: a block above the toolbar
//                        (the matters' 14-day strip), a declared toolbar
//                        (chips · my-toggle · count · search · export/import ·
//                        add · lock), rows computed by the config (its own
//                        filter chain and comparators), sort state owned by
//                        the config, and plain rows (no letter separators, no
//                        Cases column, no inline detail). Every piece is
//                        ABSENT on the thirteen and their trees are unchanged.
//                        190 GLANCE ROUND 2 [B], RULED — THE TOOLBAR IS TWO
//                        ROWS AT ALL TIMES: an ACTIONS row above the filters
//                        (the lock at the right; when unlocked — 190 [J],
//                        ruled — export and import at the far left and the
//                        add button(s) beside the lock, as the section headers
//                        do; locked, the lock alone), then the FILTER row
//                        (search · chips · prospect toggle · my-toggle · the
//                        Bento icon · count). MEASURED before, 1600/xlarge:
//                        one wrapping row put "+ Nouveau prospect" alone on a
//                        second line with the lock at its far right when
//                        unlocked, and everything on one line when locked —
//                        "the lock jumps around even on a big screen".
//   bento                190 glance round 2 [B] — the list Bento as a
//                        capability: { active, onToggle, title, pane }. The
//                        icon sits in the filter row just after the last
//                        filter (the Liste | Tableau strip above the 14-day
//                        agenda is gone), and while active the pane renders
//                        IN PLACE OF THE LIST BODY ONLY — the strip and both
//                        toolbar rows stay, and the pane is handed `fi`, the
//                        rows the filters left, so the filters drive it.
//                        A column may carry `lk`, an explicit header label
//                        key, where the `list.<et>.col.<k>` derivation does
//                        not apply; `ns` marks it non-sortable (a plain
//                        header); `ts`/`tc` put a style/className on the td
//                        itself, spread conditionally so an undeclared column
//                        adds no key.
//
// THIS COMPONENT MUST STAY HOOKLESS. The byte-identity freeze calls it as a
// plain function (recordings/57c-entitylist-freeze), which is only legal
// while no hook lives here — file plumbing like the import button's hidden
// <input> stays in the owning view, reached through toolbar.importCtl.onOpen.
//
// THE `: false` FALLBACKS ARE LOAD-BEARING and deliberately ugly: the old
// `et === "x" && …` left a literal `false` child in the tree for every entity
// the branch was not about, and this rewrite is proved by BYTE-IDENTICAL
// replay of all thirteen screens (recordings/57c-entitylist-freeze). Collapsing
// the six slots into a variable-length list renders the same pixels but not
// the same tree, so it is an IMPROVEMENT — a later, ruled act with one
// deliberate re-record, registered at 57c's close, never smuggled into this
// move.

/* 168B §1a — SORTING WHAT IS COMPUTED.
   The comparator below sorts `a[k]` — a FIELD ON THE ROW — as a string, with
   one special case for `_name`. Every column the Clients list gains in §1b–§1e
   is COMPUTED (the matters' titles, the client's standing across them, that
   standing's date, the sum of what is due), so none of them has a field to
   sort by and a string sort would be wrong for two of them anyway.
   THE FOUR RULES 2 — union, don't select: a column MAY declare `sv(it, ctx)`
   answering with a string, a number or a Date, and the comparator uses it when
   it is there. A column without `sv` sorts exactly as it did, byte for byte;
   `ns` still marks a column non-sortable. */
export type SortValue = string | number | Date | null | undefined;
/** 190 glance round 2 [B] — the list Bento as the matter lists declare it.
 *  `pane` is React's own FC over the FILTERED rows: the row array is the
 *  "props" and the capability context rides FC's second parameter, exactly
 *  as `detailActions` is read (183's rule — a function type with a named
 *  parameter is counted by no-unused-vars, and an imported type is not). */
export type BentoCapability = Readonly<{
  active: boolean;
  onToggle: () => void;
  title: string;
  pane: React.FC<unknown[]>;
  /** 190 [I] — the template picker (BentoBoard.tsx's BentoPicker, from the
   *  host's own `useBento` state): rendered in the icon's row, immediately
   *  right of it, while the pane is open. */
  picker?: React.ReactNode;
}>;
/** A row and the capability context, as loosely as this file knows them —
 *  every capability here is handed the same pair, and `Record<string,
 *  unknown>` says so without spending an `any` (`scripts/lint/ratchets.js`
 *  holds this file at 8 and it is at 8). */
export type SortFn = (
  it: Record<string, unknown>,
  ctx: Record<string, unknown>,
  rowCtx: unknown,
) => SortValue;

/** Order two `sv` answers. Numbers and Dates compare NUMERICALLY (a string sort
 *  puts 9 after 10 and 2026-01-02 before 2026-01-10 only by luck of the zero
 *  padding); anything else compares with `localeCompare`, which is what the
 *  field path has always used. An ABSENT value (null/undefined) is not the
 *  empty string — a client with no matters has no standing and no date at all,
 *  and lumping those in with "" would interleave them among real values — so
 *  absence sorts after every present value in the ascending direction, and
 *  `ls.d` flips that with everything else. */
export const cmpSortValues = (a: SortValue, b: SortValue): number => {
  if (a == null || b == null) return a == null ? (b == null ? 0 : 1) : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b));
};

/* 189 §B — a column whose header is the firm's own text (a custom-field
   column): rendered as is; every other header goes through its key. */
const rawLabel = (c: { raw?: boolean; l?: unknown }): string | null =>
  c.raw === true && typeof c.l === "string" ? c.l : null;


/* 211 §21 — a details row may say when it shows (`show`) and whose words it reads
   (`lk`); a row that says neither renders as it always has. Read typed, once. */
interface DetailRowOpts {
  k?: unknown;
  show?: unknown;
  lk?: unknown;
}
function detailShown(c: DetailRowOpts, it: unknown): boolean {
  return typeof c.show !== "function" || Boolean(Reflect.apply(c.show, undefined, [it]));
}
function detailKey(c: DetailRowOpts, et: string): string {
  return typeof c.lk === "string" ? c.lk : `list.${et}.detail.${String(c.k)}`;
}
/** 211 §19 — the text an entity's `searchText` adds to a row's (a client's numbers in its collective cases). */
function extraSearchText(fn: unknown, row: unknown, ctx: unknown): string {
  return typeof fn === "function" ? String(Reflect.apply(fn, undefined, [row, ctx])) : "";
}

/** 213B §7 [RULED 41] — whether a row is archived, read through `unknown` (the rows are untyped here). */
const isArchivedRow = (row: unknown): boolean => (row as { archived?: unknown } | null | undefined)?.archived === true;

const EntityList = ({
  items,
  cols,
  et,
  am,
  // BRIEF 108 §9 — optional modifier class on the <table> (the matter lists
  // pass "elist-matters" to lift the 28ch cell cap; see App.css).
  tableClass,
  label,
  canEditRow,
  canDeleteRow,
  deleteConfirm,
  onDelete,
  detailBadge,
  archiveAction,
  /* 189 §H — a per-entity action slot in the detail pane's header, beside the
     archive verb (ecfg's `cl` mounts "Write with a template"); `(it, ctx)`
     like the others. Read once below as React's FC (an imported type: the
     row is the "props", the ctx rides FC's second parameter). */
  detailActions,
  casesCard,
  contactPersonsCard,
  linkedClientCard,
  opponentContactsCard,
  linkedOpponentCard,
  weeklyScheduleCard,
  internalRatesCard,
  // 182 A.3 — the client record's Notes section (the contact timeline).
  notesCard,
  detailFooter,
  archivedVisibility,
  rowPass,
  searchText,
  extraCols,
  rowCtx,
  casesCol,
  caseCountExtra,
  exportSheet,
  preList,
  toolbar,
  bento,
  rowsSource,
  sortCtl,
  plainRows,
  detailId,
  setDetailId,
  confirmDelete,
  setConfirmDelete,
  clBlocked,
  setClBlocked,
  t,
  toast,
  _appIsAdmin,
  _appPerms,
  currentUser,
  schedAxis, // 58 §5b — the firm schedule axis, for weeklyScheduleCard
  openModal,
  doArchiveClient,
  doUnarchiveClient,
  openMailTemplate, // 189 §H
  delClient,
  del,
  persist,
  nav,
  lp,
  cf2,
  // 168B §1f — two app services a column cell reaches for: compose an e-mail
  // through the firm's mail-client preference, and copy a value with a toast.
  // Named seams rather than direct imports, because a cell's `onClick` is
  // DRIVEN by recordings/57c-entitylist-freeze and `window.open`/`navigator`
  // are not things that harness can stub.
  composeTo,
  copyText,
  canSeeMatterDue,
  canSeeAnyDue,
  caseStatusesList,
  listSort,
  setListSort,
  clFilter,
  query,
  cases,
  collectiveCases,
  invoices,
  clients,
  setClients,
  opponents,
  setOpponents,
  opponentLawyers,
  setOpponentLawyers,
  experts,
  setExperts,
  jurisdictions,
  setJurisdictions,
  collaborators,
  setCollaborators,
  contactPersons,
  setContactPersons,
  opponentContacts,
  setOpponentContacts,
  mediators,
  setMediators,
  arbiters,
  setArbiters,
  additionalOpponents,
  setAdditionalOpponents,
}: any) => {
    // The context every capability receives — the UNION of what the moved
    // branch bodies read (rule 2: union, don't select). A capability that
    // needs a new input widens this object, never reaches around it.
    const detailActionsFn = detailActions as React.FC<unknown> | undefined;
    const bentoCap = bento as BentoCapability | undefined;
    const ctx = {
      t,
      toast,
      nav,
      lp,
      cf2,
      composeTo,
      copyText,
      persist,
      del,
      delClient,
      doArchiveClient,
      doUnarchiveClient,
      openMailTemplate: openMailTemplate as Dispatch<unknown>, // 189 §H — the setter, cast at the seam
      openModal,
      setDetailId,
      setConfirmDelete,
      setClBlocked,
      currentUser,
      schedAxis,
      _appIsAdmin,
      _appPerms,
      canSeeMatterDue,
      canSeeAnyDue,
      caseStatusesList,
      clFilter,
      query,
      cases,
      collectiveCases,
      invoices,
      clients,
      setClients,
      opponents,
      setOpponents,
      opponentLawyers,
      setOpponentLawyers,
      experts,
      setExperts,
      jurisdictions,
      setJurisdictions,
      collaborators,
      setCollaborators,
      contactPersons,
      setContactPersons,
      opponentContacts,
      setOpponentContacts,
      mediators,
      setMediators,
      arbiters,
      setArbiters,
      additionalOpponents,
      setAdditionalOpponents,
    };
    if (detailId) {
      const it = items.find((i: any) => i.id === detailId);
      if (!it) return null;
      const _editOk = canEditRow ? canEditRow(it, ctx) : true;
      const _delOk = canDeleteRow ? canDeleteRow(ctx) : true;
      /* 213B §7 [RULED 41] — WHERE A ROW CAN BE ARCHIVED (it declares
         `archiveAction` — the clients, today), THE BIN SHOWS ONLY ON AN ARCHIVED
         ROW: an active client shows « Archiver » and no bin, as a case and a
         collective case do (CaseView.tsx, CollectiveCaseView.tsx). The guarded
         delete, its refusal and the confirmation below stay as they are; a list
         with no archive keeps its bin under `_delOk` alone. */
      const _binShown = Boolean(_delOk) && (!archiveAction || isArchivedRow(it));
      return (
        <>
          <button
            className="btn-back"
            onClick={() => {
              setDetailId(null);
              setConfirmDelete(null);
              setClBlocked(null);
            }}
          >
            {t("common.back", { fallback: "← Back" })}
          </button>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              margin: "4px 0 12px",
            }}
          >
            <h3 style={{ fontSize: FS.s15, flex: 1 }}>{pname(it)}</h3>
            {detailBadge ? detailBadge(it, ctx) : false}
            {_editOk && (
              <button
                className="btn btn-ghost"
                style={{ padding: "3px 7px" }}
                onClick={() => openModal(am, it)}
              >
                ✎ {t("common.edit", { fallback: "Edit" })}
              </button>
            )}
            {archiveAction ? archiveAction(it, ctx) : false}
            {detailActionsFn ? detailActionsFn(it, ctx) : false}
            {_binShown && (
              <button
                className="btn btn-danger"
                style={{ padding: "3px 7px" }}
                onClick={() => setConfirmDelete(it.id)}
              >
                <Trash2 size={I.s} />
              </button>
            )}
          </div>
          {confirmDelete === it.id && (
            <div
              className="conflict-banner"
              style={deleteConfirm ? deleteConfirm.bannerStyle : undefined}
            >
              <span style={deleteConfirm ? deleteConfirm.spanStyle : undefined}>
                {deleteConfirm
                  ? deleteConfirm.text(ctx)
                  : t("common.delete_confirm", { fallback: "Delete?" })}
              </span>
              <button
                className="btn btn-danger"
                style={{ padding: "2px 7px" }}
                onClick={() => onDelete(it, ctx)}
              >
                {deleteConfirm
                  ? deleteConfirm.confirmLabel(ctx)
                  : t("common.yes", { fallback: "Yes" })}
              </button>
              <button
                className="btn btn-ghost"
                style={{ padding: "2px 7px" }}
                onClick={() => setConfirmDelete(null)}
              >
                {t("common.no", { fallback: "No" })}
              </button>
            </div>
          )}
          {/* Phase 36 (D) — delete refused: explain why + offer archive. */}
          {clBlocked && clBlocked.id === it.id && (
            <div className="conflict-banner" style={{ flexWrap: "wrap" }}>
              <span style={{ flex: "1 1 100%", marginBottom: 4 }}>
                {(() => {
                  const lk = clBlocked.body?.linkage;
                  const folderBlocked =
                    clBlocked.body?.folder?.hasContent ||
                    clBlocked.body?.folder?.unverifiable;
                  const parts = [];
                  if (lk?.cases) parts.push(`${lk.cases} case(s)`);
                  if (lk?.collectiveCases)
                    parts.push(`${lk.collectiveCases} collective case(s)`);
                  if (lk?.invoices) parts.push(`${lk.invoices} invoice(s)`);
                  if (lk?.contactPersons)
                    parts.push(`${lk.contactPersons} contact person(s)`);
                  if (lk?.documents) parts.push(`${lk.documents} document(s)`);
                  if (folderBlocked) parts.push("a non-empty folder");
                  return t("client.delete_blocked", {
                    fallback:
                      "This client can't be deleted" +
                      (parts.length ? " — linked to " + parts.join(", ") : "") +
                      ". Archive it instead?",
                  });
                })()}
              </span>
              <button
                className="btn btn-ghost"
                style={{ padding: "2px 7px" }}
                onClick={() => doArchiveClient(it.id)}
              >
                {t("common.archive", { fallback: "Archive" })}
              </button>
              <button
                className="btn btn-ghost"
                style={{ padding: "2px 7px" }}
                onClick={() => setClBlocked(null)}
              >
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
            </div>
          )}
          <div className="detail-grid">
            <div className="detail-card">
              <h4>{t("list.section.details", { fallback: "Details" })}</h4>
              {/* 211 §21 — a row may say when it shows (`show`) and whose words it reads (`lk`);
                  a row that says neither renders as it always has. */}
              {(cols.d as DetailRowOpts[]).filter((c) => detailShown(c, it)).map((c: any) => (
                <div key={c.k} className="detail-row">
                  <span className="detail-label">
                    {t(detailKey(c, et), { fallback: c.l })}
                  </span>
                  <span className={`detail-value ${c.mo ? "mono" : ""}`}>
                    {c.r ? c.r(it) : it[c.k] || "—"}
                  </span>
                </div>
              ))}
              {/* 204 Part Three §6 [Laurent: "OK in modal but should also be
                  in client details pane, at the bottom"] — the record's
                  internal id at the FOOT of the card: the muted token,
                  selectable, never a field — the same `#id` ModalLayout's
                  `recordId` renders in the person modals (204 §G, the same
                  key). Here and not in ecfg's nine entries because the card
                  is this component's: one definition, every list it serves
                  (the eight person lists and the jurisdictions). */}
              {(() => {
                /* typed at the seam (the row and `t` are `any` here): a cast
                   from `any` costs the ratchet nothing, a `.prop` off it does */
                const rec = it as { id?: number | string | null };
                const tr = t as TFn;
                return rec.id != null ? (
                  <div className="detail-row">
                    <span className="detail-label">
                      {tr("common.internal_id", { fallback: "Internal id" })}
                    </span>
                    <span
                      className="detail-value mono"
                      style={{ color: "var(--tm)", fontWeight: 500, userSelect: "text", cursor: "text" }}
                    >
                      #{String(rec.id)}
                    </span>
                  </div>
                ) : null;
              })()}
            </div>
            {internalRatesCard ? internalRatesCard(it, ctx) : false}
            {casesCard ? (
              casesCard(it, ctx)
            ) : (
              <div className="detail-card">
                <h4>{t("list.section.cases", { fallback: "Cases" })}</h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {lp(et, it.id)}
                </div>
              </div>
            )}
            {contactPersonsCard ? contactPersonsCard(it, ctx) : false}
            {linkedClientCard ? linkedClientCard(it, ctx) : false}
            {opponentContactsCard ? opponentContactsCard(it, ctx) : false}
            {linkedOpponentCard ? linkedOpponentCard(it, ctx) : false}
            {weeklyScheduleCard ? weeklyScheduleCard(it, ctx) : false}
          </div>
          {notesCard ? notesCard(it, ctx) : false}
          {detailFooter ? detailFooter(it, ctx) : false}
        </>
      );
    }
    const ls = listSort[et];
    /* 168B §1e — the columns this viewer actually gets. A column without a
       `gate` is kept, so the filtered array holds the same elements in the same
       order for every entity that declares none, and their trees do not move. */
    const colsT = (cols.t as { gate?: (c: Record<string, unknown>) => boolean }[]).filter(
      (c) => !c.gate || c.gate(ctx),
    );
    /* 168B §1b — THE PER-ROW CONTEXT, COMPUTED ONCE PER ROW PER RENDER.
       Keyed by the row OBJECT, not by `it.id`: identity is what a render pass
       actually has, and it cannot collide. Four of the Clients list's columns
       read one `clientMatterSummary` over the loaded matters and invoices; the
       comparator reads it too, and a comparator is called O(n log n) times. */
    const _rowCtxMemo = new Map<object, unknown>();
    const rowCtxOf = (it: object): unknown => {
      if (!rowCtx) return undefined;
      if (!_rowCtxMemo.has(it)) _rowCtxMemo.set(it, rowCtx(it, ctx));
      return _rowCtxMemo.get(it);
    };
    const cycleSort = (k: any) => {
      const cur = ls;
      let nxt;
      if (!cur || cur.k !== k) nxt = { k, d: 1 };
      else if (cur.d === 1) nxt = { k, d: -1 };
      else nxt = null;
      const upd = { ...listSort, [et]: nxt };
      setListSort(upd);
    };
    const fi = rowsSource ? rowsSource(ctx) : [...items]
      .filter((i) => {
        // Archived visibility: an entity may declare its own axis
        // (Phase 43 item 1 — the clients list's active | incl | only);
        // every other list keeps the legacy hide-until-typed rule.
        if (archivedVisibility) {
          if (!archivedVisibility(i, ctx)) return false;
        } else if (!query.trim() && i.archived) {
          return false;
        }
        if (
          !(Object.values(i)
            .filter((v) => typeof v === "string")
            .join(" ") + " " + extraSearchText(searchText, i, ctx))
            .toLowerCase()
            .includes(query.toLowerCase())
        )
          return false;
        if (rowPass && !rowPass(i, ctx)) return false;
        return true;
      })
      .sort((a, b) => {
        if (ls && ls.k) {
          const k = ls.k;
          // 168B §1a — the declared sort value, when the column declares one.
          const svCol = (colsT as { k: string; sv?: SortFn }[]).find(
            (c) => c.k === k,
          );
          if (svCol && svCol.sv)
            return (
              cmpSortValues(
                svCol.sv(a, ctx, rowCtxOf(a)),
                svCol.sv(b, ctx, rowCtxOf(b)),
              ) * ls.d
            );
          const va =
            k === "_name"
              ? a.lastName || a.companyName || a.name || ""
              : a[k] || "";
          const vb =
            k === "_name"
              ? b.lastName || b.companyName || b.name || ""
              : b[k] || "";
          return String(va).localeCompare(String(vb)) * ls.d;
        }
        return (a.lastName || a.companyName || a.name || "").localeCompare(
          b.lastName || b.companyName || b.name || "",
        );
      });
    if (!rowsSource && items.length === 0) {
      const lbl = label || "item";
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            color: "var(--tm)",
          }}
        >
          <Users size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p
            style={{
              fontSize: FS.s13,
              fontWeight: 500,
              color: "var(--tp)",
              marginBottom: 4,
            }}
          >
            {t(`list.${et}.empty.title`, { fallback: `No ${lbl}s yet` })}
          </p>
          <p style={{ fontSize: FS.s11, marginBottom: 16 }}>
            {t(`list.${et}.empty.add_first`, {
              fallback: `Add your first ${lbl} to get started.`,
            })}
          </p>
          <button
            className="btn btn-primary"
            style={{ padding: "6px 14px", fontSize: FS.s11 }}
            onClick={() => openModal(am)}
          >
            {t(`list.${et}.empty.add_btn`, { fallback: `+ Add ${lbl}` })}
          </button>
        </div>
      );
    }
    /* 195 §C.3a (found by the §C.5 sweep) — the "no results" state used to
       RETURN here, before the toolbar: harmless while the box lived above
       this component (App's page-level box), a trap once the box is this
       component's own — the first search that matched nothing took the box
       with it, and the text could not be cleared. The block now stands where
       the table would, under the toolbar. */
    const noResults =
      !rowsSource && fi.length === 0 && query ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "40px 20px",
            color: "var(--tm)",
          }}
        >
          <Search size={32} style={{ marginBottom: 10, opacity: 0.3 }} />
          <p style={{ fontSize: FS.s12 }}>
            {t("list.empty.no_results", {
              q: query,
              fallback: `No results for "${query}"`,
            })}
          </p>
        </div>
      ) : null;
    const extraColsList = extraCols ? extraCols(ctx) : [];
    /* 190 round 7 [AH] — THE TWO MATTER LISTS SCROLL SIDEWAYS WITH ARROWS
       [RULED]. With the seven counter columns they overflow, and the wrapper's
       bare `overflow-x: auto` scrolled them silently — no affordance. `HScroll`
       (SharedUI — the 14-day strip's wrapper, the taxonomy table's since round
       6 [X]) is the tree's one horizontal scroller with the left/right hints;
       it is mounted INSIDE the card wrapper, which in turn stops being a scroll
       container (`elist-wrap`, `overflow-x: clip` — 182 D.5 (6)'s own idiom)
       so the list has ONE scroller and the hints measure it. The letter-
       separated lists are untouched: their wrapper is already `clip` for the
       A–Z separator's sake, and a scroller inside it would take the
       separator's pin box back (179 A.1). The table is built ONCE and the
       matter lists hand it to `HScroll`; the letter-separated lists mount it
       exactly as before — no wrapper of any kind, not even a Fragment, because
       the 57c freeze records the ELEMENT tree by type name and a Fragment is
       a new node in it on every one of the thirteen frozen screens. */
    /* Brief 86 §2d — `elist` exists for one CSS rule: thead th does not
       wrap. Without it a header's width is decided by its COLUMN'S DATA
       (tbody td caps at 28ch with an ellipsis, App.css:493), so a column
       whose rows all render "—" collapses below its label and the label
       breaks onto two lines. That is why the two matter lists' headers
       disagreed while being produced by this one component from one
       config builder: nothing in the code differed, the rows did. The
       list scrolls sideways (the `HScroll` below since 190 round 7 [AH];
       `.table-wrap`'s own overflow before it), so a long header costs a
       scroll rather than a second line. */
    const tableEl = (
          <Table className={"elist" + (tableClass ? " " + tableClass : "")}>
            <thead>
              <tr>
                {colsT.map((c: any) => (
                  <th
                    key={c.k}
                    /* 190 round 11 [BA] — `ths`, the header cell's own style, the
                       twin of the row cell's `ts` (below): the matter lists' ENVOYÉ
                       column declares the basis bar on both. A column without one
                       renders the props it always rendered. */
                    {...(c.ns
                      ? (c.ths ? { style: c.ths } : {})
                      : {
                          style: { cursor: "pointer", userSelect: "none", ...(c.ths ?? {}) },
                          onClick: () =>
                            sortCtl ? sortCtl.onClick(c.k) : cycleSort(c.k),
                        })}
                  >
                    {/* 189 §B — a custom-field column's header is the firm's own
                        label, DATA rendered as is; every other header is a key. */}
                    {rawLabel(c as { raw?: boolean; l?: unknown }) ?? t(c.lk || `list.${et}.col.${c.k}`, { fallback: c.l })}
                    {/* 200-glance §M.2 — the one sort caret on every column that
                        sorts (`ns` columns have no click and get none): the dim ▶
                        holds the space, so the sorted heading no longer widens by a
                        glyph and re-solves its neighbours. The `sortCtl` branch's
                        `marginLeft: 2` became the primitive's 3. */}
                    {!c.ns &&
                      (sortCtl ? (
                        <SortCaret active={sortCtl.k === c.k} d={sortCtl.d as number} />
                      ) : (
                        <SortCaret active={!!ls && ls.k === c.k} d={ls?.d as number | undefined} />
                      ))}
                  </th>
                ))}
                {extraColsList.map((col: any) => (
                  <th key={col.k}>{col.label}</th>
                ))}
                {plainRows || casesCol === false ? false : (
                  <th>{t("list.col.cases", { fallback: "Cases" })}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = [];
                let lastLetter = null;
                const totalCols =
                  colsT.length +
                  extraColsList.length +
                  (casesCol === false ? 0 : 1);
                for (const it of fi) {
                  const rc = rowCtxOf(it);
                  const cells = colsT.map((c: any) => (
                    <td
                      key={c.k}
                      {...(c.ts ? { style: c.ts } : {})}
                      {...(c.tc ? { className: c.tc } : {})}
                    >
                      {c.r ? (
                        c.r(it, ctx, rc)
                      ) : c.k === "name" || c.k === "firm" ? (
                        <span className="name-cell">{it[c.k]}</span>
                      ) : c.tg ? (
                        <span className="tag">{it[c.k]}</span>
                      ) : c.mo ? (
                        <span className="mono">{it[c.k]}</span>
                      ) : (
                        it[c.k]
                      )}
                    </td>
                  ));
                  const extraCells =
                    extraColsList.length > 0 &&
                    extraColsList.map((col: any) => (
                      <td
                        key={col.k}
                        style={{ fontSize: FS.s10, color: "var(--tm)" }}
                      >
                        {col.cell(it, rc)}
                      </td>
                    ));
                  if (plainRows) {
                    rows.push(
                      <tr
                        key={it.id}
                        style={plainRows.rowStyle ? plainRows.rowStyle(it) : undefined}
                      >
                        {cells}
                        {extraCells}
                      </tr>,
                    );
                    continue;
                  }
                  const key = String(
                    it.lastName || it.companyName || it.name || "",
                  ).trim();
                  const letter = (key[0] || "#").toUpperCase();
                  if (letter !== lastLetter) {
                    lastLetter = letter;
                    rows.push(
                      <tr key={"_sep_" + letter}>
                        <td
                          colSpan={totalCols}
                          style={{
                            background: "var(--bs)",
                            color: "var(--ac)",
                            fontWeight: 700,
                            fontSize: FS.s10_5,
                            letterSpacing: 0.6,
                            padding: "6px 10px",
                            borderTop: "1px solid var(--br)",
                            borderBottom: "1px solid var(--br)",
                            /* 179 A.1 measured this separator had NEVER pinned:
                               its pin box was `.table-wrap`, whose
                               `overflow-x: auto` made it a scroll container in
                               BOTH axes (an overflow that is not `visible` on one
                               axis computes to `auto` on the other) that never
                               scrolls vertically, so a page scroll carried the
                               separator off the screen (`drive-177-sticky-header.js
                               --site=elist`: moved −393 at 1600 wide, −450 at
                               1280, past `.content`'s edge).
                               182 D.5 (6) — the wrapper of a letter-separated list
                               is now `.elist-wrap` (`overflow-x: clip`, App.css),
                               which is NOT a scroll container, so the nearest
                               scroller is `.content` and the inset cancels its top
                               padding exactly as 177 §1b did for the entry header
                               (`.teTable-page thead th`). Measured on the same
                               drive: PINNED, edge at 0 from `.content`'s visible
                               edge, no rows in the gap, at 1600 and at 1280; the
                               wrapper had no horizontal overflow at either width
                               (1348/1348, 937/937). The list's own `thead` is not
                               sticky (App.css `thead th` declares no position) —
                               a separate question, not taken here. */
                            position: "sticky",
                            top: "calc(-1 * var(--content-pad-t))",
                            zIndex: Z.stickyHead,
                            cursor: "default",
                          }}
                        >
                          {letter}
                        </td>
                      </tr>,
                    );
                  }
                  rows.push(
                    <tr key={it.id} onClick={() => setDetailId(it.id)}>
                      {cells}
                      {extraCells}
                      {casesCol === false ? false : (
                        <td>
                          {cf2(et, it.id).length +
                            (caseCountExtra ? caseCountExtra(it, ctx) : 0)}
                        </td>
                      )}
                    </tr>,
                  );
                }
                return rows;
              })()}
            </tbody>
          </Table>
    );
    const listBody = (
      <>
        {/* 182 D.5 (6) — `elist-wrap` (App.css) takes the wrapper's horizontal
            scroll away on the letter-separated lists so the A–Z separator can
            pin against `.content`; 190 round 7 [AH] — and on the matter lists
            (`plainRows`, no separators), whose scroller is now the `HScroll`
            inside it. */}
        {noResults ?? (
          <div className="table-wrap elist-wrap">
            {plainRows ? <HScroll>{tableEl}</HScroll> : tableEl}
          </div>
        )}
      </>
    );
    if (!exportSheet && !toolbar && !preList) return listBody;
    // The export button is ONE element used from two slots — the declared
    // toolbar's unlocked group, or (for a config with exportSheet and no
    // toolbar) a bare strip of its own. Only one of the two ever renders.
    const exportButton = exportSheet ? (
      <button
        className="btn btn-ghost"
        style={{ padding: "3px 9px", fontSize: FS.s10 }}
        onClick={() => {
          void (async () => {
          try {
            const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
            const rows = fi.map((i: unknown) => exportSheet.row(i, ctx));
            const ws = XLSX.utils.json_to_sheet(rows.map(xlsxRow));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, exportSheet.sheetName);
            XLSX.writeFile(wb, exportSheet.filename(ctx));
            toast(exportSheet.exportedToast(ctx, rows.length));
          } catch (e) {
            toast(
              exportSheet.failedToast(
                ctx,
                e instanceof Error && e.message ? e.message : "unknown error",
              ),
            );
          }
          })();
        }}
      >
        <Download
          size={10}
          style={{ marginRight: 3, verticalAlign: "middle" }}
        />
        {t("common.export", { fallback: "Export" })}
      </button>
    ) : null;
    // 190 [J] — the lock, read ONCE into a typed local (the toolbar is `any`
    // here and the eslint ratchet counts every read off it): `unlocked` gates
    // export, import and the add button(s) alike.
    const lockCtl = toolbar ? (toolbar.lockCtl as { locked: boolean; onToggle: () => void } | undefined) : undefined;
    const unlocked = lockCtl === undefined || !lockCtl.locked;
    /* 195 §C.3a — row 1 (the ACTIONS row) renders only on a list that
       declares an action: the thirteen party lists declare the search alone
       (their actions row is App's, above the list), and an empty 21px strip
       would otherwise have stood between it and the box. Read once, typed. */
    const tb = (toolbar ?? null) as {
      importCtl?: unknown;
      addCtl?: { can?: boolean } | null;
      /** 212 §27 [RULED 30] — the matters' subfolder check, declared by a list that offers it */
      checkCtl?: { label: string; title: string; onOpen: () => void } | null;
    } | null;
    /* 213B §21 [RULED 49] — row 1 renders for the lock, the add button(s) or
       the check; export and import live in row 2 now (below), so they no
       longer make an actions row by themselves. */
    const hasActions = !!tb && (lockCtl !== undefined || !!tb.addCtl?.can || !!tb.checkCtl);
    /* 190 [J] (ruled 2026-09-15) — export and import appear ONLY WHEN
       UNLOCKED, the rule the add buttons follow: a locked list shows its lock
       and nothing else of its actions. 213B §21 [RULED 49] — they stand at
       the RIGHT END of the filter row, under the lock, after the count
       [ADVISOR — the count just before them]: one element each, placed once. */
    const importButton = unlocked && toolbar?.importCtl ? (
      <button
        className="btn btn-ghost"
        style={{ padding: "3px 9px", fontSize: FS.s10 }}
        onClick={toolbar.importCtl.onOpen}
      >
        <Upload
          size={10}
          style={{ marginRight: 3, verticalAlign: "middle" }}
        />
        {t("common.import", { fallback: "Import" })}
      </button>
    ) : false;
    return (
      <>
        {preList ? preList(ctx) : false}
        {hasActions ? (
          /* [B] row 1 — the ACTIONS row, present at all times: the lock at
             the right; when unlocked, the add button(s) next to the lock, as
             the section headers place them (190 [J]), and the subfolder check
             just left of them (213B §21 [RULED 49]). Export and import left
             this row for the filter row's right end (ruling 49).
             `data-elist-actions` is the drive's mark. */
          <div
            data-elist-actions=""
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
              flexWrap: "wrap",
              minHeight: 21,
            }}
          >
            {/* 190 [J] — the row keeps the buttons' height while they are
                away (`minHeight`, the `btn` at FS.s10 + 3px: measured 20.6 CSS
                px against the lock's 19.6), so the filter row under it does
                not move by a pixel on unlock — "the lock jumps around" was
                [B]'s finding. Unchanged by 213B §21: the lock keeps its place,
                the row its height. */}
            <span style={{ flex: 1 }} />
            {/* 212 §27 [RULED 30] put the matters' subfolder check right
                after « Importer »; 213B §21 [RULED 49] — it stands here, just
                left of the add button(s), away while locked as they are. The
                toolbar is WIDENED, not forked (139 §2.5's way with `addCtl`):
                a list that declares no `checkCtl` renders exactly what it
                rendered before. */}
            {unlocked && tb.checkCtl ? (
              <button
                className="btn btn-ghost"
                style={{ padding: "3px 9px", fontSize: FS.s10 }}
                title={tb.checkCtl.title}
                onClick={tb.checkCtl.onOpen}
              >
                <Folder
                  size={10}
                  style={{ marginRight: 3, verticalAlign: "middle" }}
                />
                {tb.checkCtl.label}
              </button>
            ) : false}
            {unlocked && toolbar.addCtl && toolbar.addCtl.can ? (
              <>
                <AddButton
                  label={toolbar.addCtl.label(ctx)}
                  onClick={toolbar.addCtl.onAdd}
                  style={{ padding: "3px 9px" }}
                />
                {/* 139 §2.5 — the widened `addCtl`'s second button, under
                    the same `can`: one permission governs creating a
                    matter, tagged or not (§2.1 rule 2 — no new permission
                    key family). Absent `second`, this renders exactly the
                    one button it always did. */}
                {toolbar.addCtl.second ? (
                  <AddButton
                    label={toolbar.addCtl.second.label(ctx)}
                    onClick={toolbar.addCtl.second.onAdd}
                    style={{ padding: "3px 9px" }}
                  />
                ) : false}
              </>
            ) : false}
            {lockCtl ? (
              <LockButton
                locked={lockCtl.locked}
                onToggle={lockCtl.onToggle}
              />
            ) : false}
          </div>
        ) : false}
        {toolbar ? (
          /* [B] row 2 — the FILTER row; since 213B §21 [RULED 49] it ends
             with the count, then export and import (unlocked only), under
             the lock. `data-elist-filter` is the drive's mark. */
          <div
            data-elist-filter=""
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            {/* 158 §8 — THE SEARCH BOX SITS BESIDE WHAT IT SEARCHES. It was
                at the far right of this row, past the status chips, the
                prospect toggle, the "my" toggle and the count spacer — while
                REF and TITRE, the columns one actually searches, start at the
                far left. The eye crossed the screen on every search. Same
                width, same handlers, same element: only its place in the row
                changed, and it now leads the row it filters. */}
            {/* 213B §21 — the box YIELDS before the row wraps: 200 px where the row
                has room, down to 120 px where it has not (the Dutch chips at 1440 px
                left the row 57 px short for Export and Import — measured by the
                drive, the row 25 → 54 px on unlock). A wrapping flex row WRAPS before
                it shrinks any item (the line is laid out by each item's basis), so
                the basis is 120 px and the box GROWS back to 200 px with the row's
                free space (`flex: 1 1 120px`, `maxWidth: 200`) — the tail group's
                auto margin takes what is left, so it stays at the right end.
                `.search-box input` carries `min-width: 0` for this (App.css): an
                input's intrinsic width is the flex floor otherwise. */}
            <div className="search-box" style={{ flex: "1 1 120px", minWidth: 120, maxWidth: 200 }}>
              <Search size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
              <input
                placeholder={t("common.search_placeholder")}
                value={toolbar.search.value}
                onChange={(e) => toolbar.search.onChange(e.target.value)}
              />
            </div>
            {toolbar.chips ? (
              <SegmentedControl
                value={toolbar.chips.value}
                options={toolbar.chips.options}
                onChange={toolbar.chips.onChange}
                counts={toolbar.chips.counts}
              />
            ) : false}
            {/* 139 §2.3 — the prospect toggle, beside the chips: a SECOND
                PeriodFilter, because the tree already owns an n-state pill and
                a three-state control on an orthogonal axis is exactly what it
                is for. Not a fourth chip — a second control. */}
            {toolbar.prospectToggle ? (
              <SegmentedControl
                value={toolbar.prospectToggle.value}
                options={toolbar.prospectToggle.options}
                onChange={toolbar.prospectToggle.onChange}
                counts={toolbar.prospectToggle.counts}
              />
            ) : false}
            {toolbar.myToggle ? (
              <button
                type="button"
                onClick={toolbar.myToggle.onToggle}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  borderRadius: RAD.r20,
                  border: "1px solid var(--br)",
                  padding: "2px 10px",
                  fontSize: FS.s10,
                  fontWeight: 600,
                  background: toolbar.myToggle.active ? "var(--as)" : "var(--bc)",
                  color: toolbar.myToggle.active ? "var(--ac)" : "var(--tm)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {toolbar.myToggle.label(ctx)}
                <span
                  style={{
                    fontSize: FS.s9,
                    fontWeight: 400,
                    color: "var(--tm)",
                    marginLeft: 3,
                  }}
                >
                  {toolbar.myToggle.count}
                </span>
              </button>
            ) : false}
            {/* [B] — the Bento icon, just after the last filter: the case
                Tasks header's own button (CaseTasksSection.tsx), lit while
                the pane is open. */}
            {bentoCap ? (
              /* 190 round 6 [Y] — the icon takes 21, the header's size (it
                 was 14, too small); [AA] — it GLOWS while the list's Bento is
                 open: the lock's three declarations (SharedUI OPEN_MODE_GLOW)
                 replace the `--ac` + opacity tell that carried the same state
                 (`aria-pressed` stays for the reader that cannot see).
                 190 round 9 [AS] — the icon anchors the picker: beside it
                 where this row has room, below it, flush left, where not
                 (BentoBoard's BentoPickerAnchor; this row's gap is 8). */
              <BentoPickerAnchor gap={8} picker={bentoCap.active ? bentoCap.picker : null}>
              <button
                type="button"
                className="scIcHov"
                title={bentoCap.title}
                aria-pressed={bentoCap.active}
                style={{
                  background: bentoCap.active ? OPEN_MODE_GLOW.background : "none",
                  border: bentoCap.active ? OPEN_MODE_GLOW.border : "none",
                  animation: bentoCap.active ? OPEN_MODE_GLOW.animation : undefined,
                  borderRadius: RAD.r6,
                  cursor: "pointer",
                  padding: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: bentoCap.active ? OPEN_MODE_ICON : "var(--tp)",
                }}
                onClick={bentoCap.onToggle}
              >
                <SquareKanban size={21} />
              </button>
              </BentoPickerAnchor>
            ) : false}
            {/* 213B §21 [RULED 49] — the count, then Export, then Import, as ONE
                group pushed to the row's right end under the lock [ADVISOR — the
                count just before them]; a group, so that where the row MUST wrap
                (the Dutch chips at 1440 px: measured by the drive, the row 25 →
                54 px on unlock) the three go to the next line together and stay
                under the lock, rather than Import alone at the far left of a new
                line. Export and Import only when unlocked (190 [J]); locked, the
                group holds the count alone, where it stood. */}
            <div data-elist-tail="" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                {t("cc.count_of", { shown: fi.length, total: items.length })}
              </span>
              {unlocked ? exportButton : false}
              {importButton}
            </div>
          </div>
        ) : (
          exportButton && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
                flexWrap: "wrap",
              }}
            >
              {exportButton}
            </div>
          )
        )}
        {/* [B] (e)/(f) — the Bento pane in place of the LIST BODY ONLY, over
            the rows the filters left. */}
        {bentoCap?.active ? bentoCap.pane(fi as unknown[], ctx) : listBody}
      </>
    );
};

export default EntityList;
